"use client";

import { FormEvent, useEffect, useState } from "react";
import { Ban, ChevronLeft, ChevronRight, CircleCheckBig, ListChecks, Percent, ShieldCheck, X } from "lucide-react";
import { drawApprovedAmount, drawWithheldAmount, holdbackForAmount } from "@/lib/financial";
import { homeownerDecisionLabel, validateHomeownerDecisionDraft, type HomeownerDecisionDraft, type SowDecision } from "@/lib/homeownerReview";
import type { Draw, DrawInvoice, SowItem } from "@/lib/types";

type DecisionMode = "approve" | "holdback" | "changes" | null;

const money = (value: number) => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(value);

function savedDecisionLabel(draw: Draw) {
  if (draw.status === "CLIENT_REVIEW") return "Needs Your Decision";
  if (draw.status === "CHANGES_REQUESTED") return "Changes Requested";
  if (draw.status === "RECEIVED") return "Approved — Payment Received";
  return Number(draw.holdbackPercent || 0) > 0 ? `Approved with ${draw.holdbackPercent}% Holdback` : "Approved in Full";
}

function modeForDraft(draft: HomeownerDecisionDraft | null): DecisionMode {
  if (draft?.decision === "APPROVE_FULL") return "approve";
  if (draft?.decision === "APPROVE_WITH_HOLDBACK") return "holdback";
  if (draft?.decision === "REQUEST_CHANGES") return "changes";
  return null;
}

export function ClientSowDecision({
  draw,
  sow,
  invoice,
  position,
  total,
  previousDrawId,
  nextDrawId,
  focusDraw,
  focusSummary,
  draft,
  saveDraft,
  setMessage,
}: {
  draw: Draw;
  sow: SowItem;
  invoice: DrawInvoice;
  position: number;
  total: number;
  previousDrawId: string | null;
  nextDrawId: string | null;
  focusDraw: (drawId: string) => void;
  focusSummary: () => void;
  draft: HomeownerDecisionDraft | null;
  saveDraft: (draft: HomeownerDecisionDraft) => void;
  setMessage: (value: string) => void;
}) {
  const [mode, setMode] = useState<DecisionMode>(() => modeForDraft(draft));
  const [percent, setPercent] = useState(() => draft?.decision === "APPROVE_WITH_HOLDBACK" ? String(draft.holdbackPercent) : "5");
  const [reason, setReason] = useState(() => draft?.reason || "");
  const [activePhoto, setActivePhoto] = useState<string | null>(null);
  const parsedPercent = Number(percent);
  const validPercent = mode === "holdback" && Number.isInteger(parsedPercent) && parsedPercent >= 1 && parsedPercent <= 15 ? parsedPercent : 0;
  const selectionMade = mode !== null;
  const preview = mode === "approve"
    ? holdbackForAmount(draw.amount, 0)
    : mode === "holdback"
      ? holdbackForAmount(draw.amount, validPercent)
      : { approvedAmount: 0, withheldAmount: 0 };
  const previewDecision = mode === "changes"
    ? "Decline / Request Changes"
    : mode === "holdback"
      ? `Approve with ${validPercent || "—"}% Holdback`
      : mode === "approve"
        ? "Approve in Full"
        : "No decision selected";
  const isPending = draw.status === "CLIENT_REVIEW";
  const photos = draw.photoDataUrls || [];

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setMode(modeForDraft(draft));
      setPercent(draft?.decision === "APPROVE_WITH_HOLDBACK" ? String(draft.holdbackPercent) : "5");
      setReason(draft?.reason || "");
    });
    return () => { cancelled = true; };
  }, [draft]);

  const saveChoice = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!mode) {
      setMessage(`Choose a decision for ${sow.title} before saving its draft.`);
      window.setTimeout(() => setMessage(""), 3400);
      return;
    }

    const decision: SowDecision = mode === "changes" ? "REQUEST_CHANGES" : mode === "holdback" ? "APPROVE_WITH_HOLDBACK" : "APPROVE_FULL";
    try {
      const validated = validateHomeownerDecisionDraft({
        drawId: draw.id,
        decision,
        holdbackPercent: validPercent,
        reason: mode === "approve" ? "" : reason,
      });
      saveDraft({ drawId: draw.id, decision, holdbackPercent: validated.holdbackPercent, reason: validated.reason });
      setMessage(`${sow.title} draft saved on this device. Nothing has been submitted.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The SOW draft could not be saved.");
    }
    window.setTimeout(() => setMessage(""), 4200);
  };

  const savedReason = isPending ? draft?.reason.trim() || "" : draw.holdbackReason?.trim() || "";
  const statusLabel = isPending ? draft ? `Draft — ${homeownerDecisionLabel(draft)}` : "Needs Your Decision" : savedDecisionLabel(draw);
  const statusClass = isPending ? draft ? "draft" : "pending" : "saved";

  return <section id={`sow-review-${draw.id}`} className="manager-form client-invoice-decision client-sow-decision" aria-label={`Homeowner decision for ${sow.title}`}>
    <div className="section-head">
      <div><span className="eyebrow">SOW {position} of {total}</span><h3>{sow.title}</h3><p>{invoice.requestNumber} · This decision applies only to this SOW.</p></div>
      <span className={`status-pill ${statusClass}`}>{statusLabel}</span>
    </div>
    <div className="sow-review-context">
      <div><span>SOW Scope</span><p>{sow.description}</p></div>
      <div><span>Current Request</span><p>{draw.explanation}</p></div>
    </div>
    {photos.length ? <div className="sow-review-photos" aria-label={`${sow.title} request photographs`}>{photos.map((src,index) => <button data-aqua-id={`open-sow-photo:${draw.id}:${index + 1}`} data-aqua-label={`Open ${sow.title} photograph ${index + 1}`} type="button" onClick={() => setActivePhoto(src)} key={`${src.slice(-20)}-${index}`} aria-label={`Open ${sow.title} photograph ${index+1}`}><img src={src} alt={`${sow.title} progress photograph ${index+1}`}/></button>)}</div> : null}
    <div className="client-decision-summary">
      <div><span>Requested</span><strong>{money(draw.amount)}</strong></div>
      <div><span>Your Decision</span><strong>{isPending ? previewDecision : savedDecisionLabel(draw)}</strong></div>
      <div><span>Approved</span><strong>{isPending ? selectionMade ? money(preview.approvedAmount) : "—" : money(drawApprovedAmount(draw))}</strong></div>
      <div><span>Held Back</span><strong>{isPending ? selectionMade ? money(preview.withheldAmount) : "—" : money(drawWithheldAmount(draw))}</strong></div>
    </div>
    {savedReason ? <div className="metric-card change-reason-card"><span>{mode === "changes" || draw.status === "CHANGES_REQUESTED" ? "Requested Change Explanation" : "Holdback Explanation"}</span><strong>{savedReason}</strong></div> : null}
    {isPending ? <form data-aqua-id={`client-sow-decision:${draw.id}`} data-aqua-kind="form" onSubmit={saveChoice}>
      <div className={`decision-safety-note ${selectionMade ? "preview" : "waiting"}`} role="status" aria-live="polite"><ShieldCheck size={20}/><div><strong>{selectionMade ? "Draft only — not submitted" : "No decision selected"}</strong><span>{selectionMade ? "Save this draft to keep it editable on this device. The invoice is submitted only from the final request confirmation." : "Choose one option below. Simply opening or reviewing this SOW does not approve it."}</span></div></div>
      <div className="client-decision-choices" role="group" aria-label={`Choose decision for ${sow.title}`}>
        <button data-aqua-id={`sow-decision-approve:${draw.id}`} type="button" className={mode === "approve" ? "active" : ""} aria-pressed={mode === "approve"} onClick={() => setMode("approve")}><CircleCheckBig size={18}/><span><strong>Approve in Full</strong><small>Draft approval for 100% of this SOW</small></span></button>
        <button data-aqua-id={`sow-decision-holdback:${draw.id}`} type="button" className={mode === "holdback" ? "active" : ""} aria-pressed={mode === "holdback"} onClick={() => setMode("holdback")}><Percent size={18}/><span><strong>Approve with Holdback</strong><small>Draft a 1–15% holdback with explanation</small></span></button>
        <button data-aqua-id={`sow-decision-changes:${draw.id}`} type="button" className={mode === "changes" ? "active deny" : "deny"} aria-pressed={mode === "changes"} onClick={() => setMode("changes")}><Ban size={18}/><span><strong>Decline / Request Changes</strong><small>Draft what Aqua Homes must change</small></span></button>
      </div>
      {mode === "holdback" ? <label>Holdback percentage<input data-aqua-field={`sow-holdback-percent-${draw.id}`} name="holdbackPercent" type="number" min="1" max="15" step="1" inputMode="numeric" value={percent} onChange={(event) => setPercent(event.target.value)} required /></label> : null}
      {mode && mode !== "approve" ? <label>{mode === "changes" ? "Change explanation" : "Holdback explanation"}<textarea data-aqua-field={`sow-decision-reason-${draw.id}`} name="decisionReason" rows={4} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} required /></label> : null}
      <button data-aqua-id={`sow-decision-submit:${draw.id}`} data-aqua-label={draft ? `Update ${sow.title} draft choice` : `Save ${sow.title} draft choice`} data-aqua-consequence="state_change" className={mode === "changes" ? "secondary-button deny-decision-button" : "primary"} type="submit" disabled={!mode}>{draft ? "Update Draft Choice" : "Save Draft Choice"}</button>
    </form> : <div className="saved-decision-note" role="status"><CircleCheckBig size={19}/><span>This decision is saved. It remains visible here while the rest of this request is under review.</span></div>}
    <nav className="sow-review-navigation" aria-label={`Move through requested SOWs from ${sow.title}`}>
      <button data-aqua-id={`sow-review-previous:${draw.id}`} data-aqua-label={`Review previous SOW without saving ${sow.title}`} type="button" className="secondary-button" disabled={!previousDrawId} onClick={() => previousDrawId && focusDraw(previousDrawId)}><ChevronLeft size={18}/><span>Review Previous — No Save</span></button>
      <span className="sow-review-position">SOW {position} of {total}</span>
      {nextDrawId ? <button data-aqua-id={`sow-review-next:${draw.id}`} data-aqua-label={`Review next SOW without saving ${sow.title}`} type="button" className="secondary-button" onClick={() => focusDraw(nextDrawId)}><span>Review Next — No Save</span><ChevronRight size={18}/></button> : <button data-aqua-id={`sow-review-summary:${draw.id}`} data-aqua-label="Review request summary without saving this SOW" type="button" className="secondary-button" onClick={focusSummary}><ListChecks size={18}/><span>Review Summary — No Save</span></button>}
    </nav>
    {activePhoto ? <div className="photo-lightbox" role="dialog" aria-modal="true" aria-label="SOW photograph viewer" onClick={() => setActivePhoto(null)}><button data-aqua-id={`close-sow-photo:${draw.id}`} data-aqua-label="Close SOW photograph" type="button" className="lightbox-close" onClick={() => setActivePhoto(null)} aria-label="Close photograph"><X size={24}/></button><div className="lightbox-stage" onClick={(event) => event.stopPropagation()}><img src={activePhoto} alt={`Enlarged ${sow.title} progress photograph`}/></div></div> : null}
  </section>;
}
